import React, { useState } from "react";
import {
    TextField,
    Button,
    Box,
    MenuItem
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const EditableForm = ({
    title,
    data,
    fields,
    onChange,
    onSubmit,
    columns = 2,
    hideSubmitButton = false
}) => {
    const { t } = useTranslation();
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        onChange({ ...data, [name]: value });
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleSubmit = () => {
        const newErrors = {};
        fields.forEach((field) => {
            const isRequired = field.required ?? false;
            if (isRequired && !data[field.name]?.toString().trim()) {
                newErrors[field.name] = t("errors.required");
            }
        });

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            onSubmit(data);
        }
    };

    return (
        <Box>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                }}
            >
                {fields.map(({ name, label, type = "text", required = false, options }) => (
                    <Box
                        key={name}
                        sx={{
                            flex: {
                                xs: "100%",
                                sm: "calc(50% - 8px)",
                            },
                        }}
                    >
                        <TextField
                            fullWidth
                            select={type === "select"}
                            type={type !== "select" ? type : undefined}
                            label={label}
                            name={name}
                            value={data[name] || ""}
                            onChange={handleChange}
                            error={!!errors[name]}
                            helperText={errors[name]}
                            InputLabelProps={{
                                ...(type === "date" ? { shrink: true } : {}),
                                sx: {
                                    color: "#666",
                                    "&.Mui-focused": { color: "#000" },
                                },
                            }}
                            InputProps={{
                                sx: {
                                    "& input, & .MuiSelect-select": {
                                        color: "#000", // Text color for input and select dropdown
                                    },
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#999",
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#666",
                                    },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#333",
                                    },
                                },
                            }}
                            SelectProps={{
                                IconComponent: (props) => (
                                    <ArrowDropDownIcon {...props} sx={{ color: '#000 !important' }} /> // Set the icon color to black
                                ),
                            }}
                        >
                            {type === "select" &&
                                options?.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value} sx={{ color: "#000" }}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                        </TextField>
                    </Box>
                ))}
            </Box>
            {!hideSubmitButton && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        sx={{
                            bgcolor: "#444",
                            color: "#fff",
                            fontWeight: 500,
                            "&:hover": {
                                bgcolor: "#333",
                            },
                        }}
                    >
                        {t("editableForm.saveChanges")}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default EditableForm;
